/**
 * Helper: Product
 * -----------------------------------------------------------------------------
 * Product helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */
import { convertSnakeToSentenceCase } from '~/helpers/convert'
import { formatProductTitle } from '~/helpers/format'
import { fetchJsonView, getMetafield } from '~/helpers/general'

/**
 * Find an option with a matching name from shop metafields color option string
 * @param product - Product from GraphQL response
 * @returns {{name: String, position: Number, values: String[]}|undefined}
 */
export function getColorOptions(product) {
  const colors = cnvs.metafields.global.colorOptions || []
  return product.options?.filter(
    (option) => colors.includes(option.name.toLowerCase()) && option.values,
  )
}

/**
 * Get images array from product. Can return media or images
 * @param {Object} product - Liquid or formatted storefront product
 * @param {Boolean} [preferMedia] - If set to false, prefer returning images
 * to media
 * @returns {*|[]|HTMLCollectionOf<HTMLImageElement>|*[]}
 */
export function getImages(product, preferMedia = true) {
  const mediaLen = product?.media?.length || 0
  const imagesLen = product?.images?.length || 0

  if (preferMedia) {
    return mediaLen > 0 ? product.media : product.images || []
  }

  return imagesLen > 0 ? product.images : product.media || []
}

/**
 * Get products by sku using product variant metafield
 * @param {Array<String|Number>} skus - Array of skus
 * @returns {Promise<Object[]>}
 */
export async function getImportedOrderProducts(skus) {
  if (!skus.length) {
    return []
  }

  try {
    const url = new URL(
      `${cnvs.routes.collections}/all`,
      location.origin,
    )

    return (
      await fetchJsonView(
        `${url.href}?filter.v.m.data.sku=${skus.join(',')}&view=json`,
      )
    ).products || []
  } catch (error) {
    cnvs.ReportError(
      `[Imported Order] Failed to load line item - ${skus.join(', ')}`,
      error,
    )
    return []
  }
}

/**
 * Get line item properties should be added to every add to cart action
 * Will append metafield values based on the shop metafield string of namespaces
 * @param variant - The variant that is being added to cart.
 * @param product - The product the variant belongs to.
 * @returns {Object}
 */
export function getLineItemProperties(variant, product) {
  const strings = cnvs.strings.product.notifications

  let key = ''
  const props = {
    ...getKlaviyoLineItemProperties(variant, product),
  }
  const metafields = cnvs.metafields.cart.lineItem || []

  /**
   * Handle all messaging namespace metafields
   */
  const isPreorder = product.tags.includes('preorder')
  const isBackordered = getMetafield({
    metafields: variant?.metafields || [],
    metaString: 'product_details.back_order',
  })
  const isMadeToOrder = getMetafield({
    metafields: variant?.metafields || [],
    metaString: 'product_details.made_to_order',
  })

  const messages = getMessagingMetafields(product, variant)
  messages.forEach((meta) => {
    if (
      !meta.value ||
      typeof meta.value !== 'string' ||
      (meta.key.toLowerCase() === 'preorder' && !isPreorder) ||
      (meta.key.toLowerCase() === 'back_order' && !isBackordered)
    ) {
      return
    }

    if (isBackordered && meta.key.toLowerCase() === 'back_order') {
      key = strings.backorder_cart_prefix || 'Backordered'
      props[key] = meta.value.toLowerCase().startsWith('backordered:')
        ? meta.value.replace(/backordered:/i, '').trim()
        : meta.value
    } else {
      props[convertSnakeToSentenceCase(meta.key, true)] = meta.value
    }
  })

  /**
   * Get messaging for made to order and final sale.
   * If there was no backorder messaging, add the fallback message
   */
  metafields.forEach((metaStr) => {
    const themeString = getMessagingByThemeString({
      object: variant,
      metaStr,
      keySuffix: '_cart',
    })

    if (!themeString) {
      return
    }

    key = strings[`${themeString.key}_cart_prefix`]

    if (!key || props[key]) {
      return
    }

    props[key] = themeString.string
  })

  if (isMadeToOrder) {
    if (variant?.inventory > 0) {
      delete props[strings['final_sale_cart_prefix']]
      delete props[strings['made_to_order_cart_prefix']]
    } else {
      props._madeToOrder = 'true'
    }
  }

  if (isBackordered) {
    props._backorder = 'true'
  }

  return props
}

/**
 * Get Klaviyo line item properties which should be added to every ATC action
 * Will append metafield values based on the shop metafield string of namespaces
 * @param variant - The variant that is being added to cart.
 * @param product - The product the variant belongs to.
 * @returns {Object}
 */
export function getKlaviyoLineItemProperties(variant, product) {
  const klaviyoMetafields = cnvs.metafields.klaviyo || {}
  const {
    products: klaviyoProductMeta = [],
    variants: klaviyoVariantMeta = [],
  } = klaviyoMetafields

  const klaviyoProps = {}

  function parseKlaviyoMetafields(accessor, metafields) {
    const [namespace, metaKey] = accessor.split('.')
    const field = metafields.find((meta) => meta.namespace === namespace &&
      meta.key === metaKey)

    const val = field?.value

    if (!val) {
      return
    }

    klaviyoProps[`_klaviyo.${accessor}`] = val.value ? val.value : val
  }

  klaviyoProductMeta.forEach(
    (metaAccessor) => parseKlaviyoMetafields(metaAccessor, product.metafields),
  )

  klaviyoVariantMeta.forEach(
    (metaAccessor) => parseKlaviyoMetafields(metaAccessor, variant.metafields),
  )

  return klaviyoProps
}

/**
 * Get messaging using metafields and product notification string keys
 * @param {Object} object - The object where metafields live, such as variant
 * @param {String} metaStr - The metafield in stringified dot notation
 * @param {String} [keySuffix] - Suffix to add to the metafield key when
 * checking notification strings
 * @returns {{string: string, key}|null}
 */
export function getMessagingByThemeString({ object, metaStr, keySuffix = '' }) {
  const metafield = getMetafield({
    metaString: metaStr,
    metafields: object.metafields,
    fullData: true,
  }) || {}

  const string = cnvs.strings.product.notifications[`${metafield.key}${keySuffix}`]
  if (
    !metafield.value ||
    !string ||
    metafield.namespace === 'messaging'
  ) {
    return null
  }

  return { key: metafield.key, string }
}

/**
 * Get all product and variant metafields where namespace equals "messaging"
 * @param product - Product object
 * @param [variant] - A selected variant, if available
 * @returns {[]}
 */
export function getMessagingMetafields(product, variant) {
  const metafields = [...product?.metafields || [], ...variant?.metafields || []]
  return metafields.filter((meta) => meta.namespace === 'messaging')
}

/**
 * Retrieve the short title from product tags if available;
 * otherwise, fallback to the default title.
 * @param {Object} product - The product object.
 * @param {Boolean} [formatTitle] - format the default title.
 * @returns {String} - Short product title.
 */
export function getShortTitle(product, formatTitle) {
  const tags = product.tags

  if (tags && Array.isArray(tags)) {
    const shortTitleTag = tags.find((tag) => tag.includes('short-title:'))

    if (shortTitleTag) {
      return shortTitleTag.split(':')[1].trim()
    }
  }

  return formatTitle ? formatProductTitle(product.title) : product.title
}

/**
 * Get option values sorted based on global order and numerically
 * @param {String[]} optionValues
 * @returns {String[]}
 */
export function getSortedOptionValues(optionValues) {
  const globalOrder = cnvs.metafields.global.optionsOrder || []
  const cache = {}
  const values = []

  optionValues.forEach((value) => {
    cache[value.toLowerCase()] = value
  })

  globalOrder.forEach((value) => {
    if (!cache[value]) {
      return
    }
    values.push(cache[value])
    delete cache[value]
  })

  const cacheValues = Object.values(cache)
  const allNumerical = cacheValues.every((value) => !isNaN(value))

  if (allNumerical) {
    cacheValues.sort(
      (a, b) => {
        return a - b
      },
    )
  }

  return [...values, ...cacheValues]
}

/**
 * Get product style number from product tag.
 * @param product
 * @returns {string}
 */
export function getStyleNumber(product) {
  if (!product?.tags) {
    return ''
  }
  const style = product.tags.find((tag) => tag.startsWith('style_no:'))
  if (!style) {
    return ''
  }
  return style.replace('style_no:', '').trim()
}

/**
 * Query b2b prices for a given product handle.
 * @see product.price.liquid
 * @param {String} handle - A product handle
 * @returns {Boolean|Object}
 */
export async function queryB2bPrice(handle) {
  const DEFAULT_RESPONSE = false

  try {
    if (!cnvs.customer.b2b || !handle || typeof handle !== 'string') {
      return DEFAULT_RESPONSE
    }

    const response = await fetch(
      `${cnvs.routes.products}/${handle}?view=price`,
    )
    const json = await response.json()

    return json.ok === false ? DEFAULT_RESPONSE : json
  } catch (error) {
    cnvs.ReportError('[queryB2bPrice]', error)
    return DEFAULT_RESPONSE
  }
}

/**
 * Query multiple products b2b prices.
 * @param {Array} handles - An array of product handles
 * @returns {Object} - Map of pricebooks where the key is the product
 * handle.
 */
export async function queryB2bPrices(handles) {
  try {
    if (!cnvs.customer.b2b || !Array.isArray(handles) || !handles.length) {
      return {}
    }

    const responses = await Promise.allSettled(handles.map(queryB2bPrice))
    const priceBooks = {}
    responses.forEach((response) => {
      if (!response.value) {
        return
      }
      priceBooks[response.value.product.handle] = response.value
    })

    return priceBooks
  } catch (error) {
    cnvs.ReportError('[queryB2bPrices]', error)
    return {}
  }
}

/**
 * Query and attach pricebook to product object reference using the property
 * `b2bPriceBook`. The pricebook is used in `formatProduct` to override
 * product price and variant prices.
 * @param {Object} product - Product object
 */
export async function augmentB2bPrice(product) {
  if (!product) {
    return
  }

  const priceData = await queryB2bPrice(product.handle)
  product.b2bPriceBook = priceData
}

/**
 * Query pricebooks and attach them to the given product objects using the
 * property `b2bPriceBook`. The pricebook is used in `formatProduct` to
 * override product price and variant prices.
 * @param {Array} products - A list of product objects
 */
export async function augmentB2bPrices(products) {
  if (!Array.isArray(products) || !products.length) {
    return
  }

  const handles = products.filter(Boolean).map((product) => product.handle)
  const priceData = await queryB2bPrices(handles)
  products.forEach((product) => {
    if (!product || !priceData[product.handle]) {
      return
    }
    product.b2bPriceBook = priceData[product.handle]
  })
}

/**
 * Get addon product references from product metafield
 * @param {object} product - Liquid or graphql formatted product
 * @returns {object | undefined}
 */
export function getAddOns(product) {
  return product?.metafields?.find((metafield) => {
    return metafield.key === 'products' && metafield.namespace === 'add_on'
  })?.value
}

/**
 * Check if addon product is in cart
 * @param {array} products
 * @param {array} cartItems
 * @returns {boolean}
 */
export function isAddOnInCart(product, cartItems) {
  return getAddOns(product)?.products.some(
    (product) => cartItems.some((item) => item.product_id === product.id),
  ) ?? false
}

/**
 * Check if product should apply "notify me" regardless of inventory
 * @param {object} product - Liquid or graphql formatted product
 * @returns {boolean}
 */
export function isBisOverride(product) {
  return product?.tags.includes('notify-bis-in-stock')
}
